import { useAccount } from "@qw/auth";
import {
  type PlaybookTriggerFragment,
  useDeletePlaybookTriggerByIdMutation,
  useMyPlaybookTriggersQuery,
  useSetPlaybookTriggerMutation,
} from "@qw/graphql";
import { SCHEDULE_TRIGGER_EVENT_TYPE } from "@qw/qw-common";
import { Button, Icon, Spinner, useToast } from "@qw/ui";
import { useState } from "react";
import { PlaybookPicker } from "./playbook-picker";
import { ScheduleTimeSelect } from "./schedule-time-select";

const DEFAULT_SCHEDULE_TIME = "09:00";

const scheduleTime = (trigger: PlaybookTriggerFragment): string | undefined => {
  const time = trigger.triggerConfig?.time;
  return typeof time === "string" ? time : undefined;
};

// `lastFiredOn` is a date-only column holding the Chicago day it fired, serialized as
// midnight UTC, so it must be formatted in UTC or it renders as the previous day.
const formatLastFired = (lastFiredOn: string) =>
  new Date(lastFiredOn).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

interface ScheduleTriggersSectionProps {
  allPlaybooks: Array<{ id: string; userId: string; name?: string | null; description?: string | null }>;
  isLoading: boolean;
}

export const ScheduleTriggersSection = ({ allPlaybooks, isLoading: parentLoading }: ScheduleTriggersSectionProps) => {
  const { user } = useAccount();
  const { toast } = useToast();
  const [selectedPlaybookId, setSelectedPlaybookId] = useState<string>();
  const [newTime, setNewTime] = useState(DEFAULT_SCHEDULE_TIME);
  const { data, loading: triggersLoading, error, refetch } = useMyPlaybookTriggersQuery();
  const triggers =
    data?.myPlaybookTriggers?.filter((trigger) => trigger.eventType === SCHEDULE_TRIGGER_EVENT_TYPE) ?? [];
  // Only a playbook's owner may schedule it; the server refuses subscribers.
  const availablePlaybooks = allPlaybooks.filter(
    (playbook) => playbook.userId === user.id && !triggers.some((trigger) => trigger.playbookId === playbook.id)
  );

  const onSaved = () => {
    toast({ title: "Schedule saved", description: "Your weekday schedule has been saved." });
    refetch();
  };

  const [setTrigger, { loading: settingTrigger }] = useSetPlaybookTriggerMutation({
    onCompleted: onSaved,
    onError: (mutationError) => {
      toast({ title: "Error saving trigger", description: mutationError.message, variant: "destructive" });
    },
  });
  const [deleteTrigger, { loading: deletingTrigger }] = useDeletePlaybookTriggerByIdMutation({
    onCompleted: () => {
      toast({ title: "Schedule removed", description: "Your weekday schedule has been removed." });
      refetch();
    },
    onError: (mutationError) => {
      toast({ title: "Error removing trigger", description: mutationError.message, variant: "destructive" });
    },
  });
  const isSaving = settingTrigger || deletingTrigger;

  const scheduleInput = (playbookId: string, time: string) => ({
    input: { eventType: SCHEDULE_TRIGGER_EVENT_TYPE, playbookId, triggerConfig: { time } },
  });

  const addSchedule = (playbookId: string) => {
    setTrigger({
      variables: scheduleInput(playbookId, newTime),
      onCompleted: () => {
        setSelectedPlaybookId(undefined);
        onSaved();
      },
    });
  };

  const changeTime = (playbookId: string, time: string) => {
    setTrigger({ variables: scheduleInput(playbookId, time) });
  };

  return (
    <div className="rounded-lg border bg-card p-6 mb-6">
      <div className="flex items-start gap-4 mb-4">
        <div className="rounded-full bg-primary/10 p-3">
          <Icon icon="schedule" className="text-2xl text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold mb-1">Schedule</h2>
          <p className="text-sm text-muted-foreground">Run a playbook you own at a set time each weekday.</p>
          <p className="text-xs text-muted-foreground mt-1">Weekdays · Central (Chicago) time</p>
        </div>
      </div>
      {parentLoading || triggersLoading ? (
        <div className="flex items-center justify-center py-4">
          <Spinner />
        </div>
      ) : error ? (
        <p role="alert" className="text-sm text-destructive">
          {error.message}
        </p>
      ) : (
        <div className="space-y-4">
          {triggers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No scheduled triggers yet.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium">Active Triggers</p>
              {triggers.map((trigger) => {
                const name = trigger.playbook?.name || "(untitled playbook)";
                return (
                  <div key={trigger.id} className="flex items-center justify-between gap-3 p-3 bg-muted/50 rounded-md">
                    <div className="space-y-2">
                      <p className="text-sm font-medium">{name}</p>
                      <ScheduleTimeSelect
                        value={scheduleTime(trigger)}
                        onChange={(time) => changeTime(trigger.playbookId, time)}
                        disabled={isSaving}
                        label={`Schedule time for ${name}`}
                      />
                      <p className="text-xs text-muted-foreground">
                        {trigger.lastFiredOn ? `Last fired ${formatLastFired(trigger.lastFiredOn)}` : "Not fired yet"}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove schedule for ${name}`}
                      disabled={isSaving}
                      onClick={() => deleteTrigger({ variables: { triggerId: trigger.id } })}
                    >
                      <Icon icon="close" className="text-lg" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="space-y-3 pt-2 border-t">
            <p className="text-sm font-medium">Add New Trigger</p>
            <ScheduleTimeSelect value={newTime} onChange={setNewTime} disabled={isSaving} label="New schedule time" />
            <div>
              <p className="text-xs text-muted-foreground mb-2">Playbook to run</p>
              <PlaybookPicker
                playbooks={availablePlaybooks}
                value={selectedPlaybookId}
                onChange={setSelectedPlaybookId}
              />
            </div>
            <Button
              size="sm"
              disabled={isSaving || !availablePlaybooks.some((playbook) => playbook.id === selectedPlaybookId)}
              onClick={() => selectedPlaybookId && addSchedule(selectedPlaybookId)}
            >
              {isSaving ? <Spinner className="size-4 mr-2" /> : null}
              Add Trigger
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
